<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useWindowSize } from '@vueuse/core';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import NewDirectMessageModal from '../components/NewDirectMessageModal.vue';
import NewGroupModal from '../components/NewGroupModal.vue';
import EditGroupMembersModal from '../components/EditGroupMembersModal.vue';
import InternalReplyBox from '../components/InternalReplyBox.vue';
import BackButton from 'dashboard/components/widgets/BackButton.vue';
import wootConstants from 'dashboard/constants/globals';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { width: windowWidth } = useWindowSize();
const currentUser = useMapGetter('getCurrentUser');
const currentRole = useMapGetter('getCurrentRole');

const selectedRoomId = ref(null);
const newDmModalRef = ref(null);
const newGroupModalRef = ref(null);
const editGroupMembersModalRef = ref(null);
const roomMembers = ref([]);
const isCreatingDm = ref(false);
const isCreatingGroup = ref(false);
const messagesContainerRef = ref(null);

const rooms = computed(() => store.getters['internalRooms/getRecords']);
const activeRoomId = computed(
  () => selectedRoomId.value || Number(route.params.roomId)
);

const messages = computed(() =>
  store.getters['internalMessages/getRecords']
    .filter(m => Number(m.room_id) === activeRoomId.value)
    .sort((a, b) => a.created_at - b.created_at)
);

const isAdmin = computed(() => currentRole.value === 'administrator');

const activeRoom = computed(() =>
  rooms.value.find(r => r.id === activeRoomId.value)
);

const isMobile = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const internalListRoute = computed(() => ({
  name: 'internal_rooms_index',
  params: { accountId: route.params.accountId },
}));

const showRoomList = computed(() => !isMobile.value || !activeRoomId.value);

const showChatPanel = computed(() => !isMobile.value || activeRoomId.value);

const isGroupRoom = computed(() => activeRoom.value?.room_type === 'channel');

const canManageGroup = computed(() => {
  if (!isGroupRoom.value) return false;
  if (isAdmin.value) return true;
  const myMembership = roomMembers.value.find(
    m => Number(m.user_id) === currentUser.value?.id
  );
  return myMembership?.role === 'admin';
});

const isSentByMe = message => message.sender?.id === currentUser.value?.id;

const formatMessageTime = timestamp =>
  messageTimestamp(timestamp, 'LLL d, h:mm a');

const isImageAttachment = attachment => attachment.file_type?.includes('image');

const scrollToBottom = () => {
  nextTick(() => {
    const container = messagesContainerRef.value;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  });
};

const fetchRooms = () => store.dispatch('internalRooms/get');

const fetchRoomMembers = async roomId => {
  if (!roomId) {
    roomMembers.value = [];
    return;
  }
  const room = rooms.value.find(r => r.id === Number(roomId));
  if (room?.room_type !== 'channel') {
    roomMembers.value = [];
    return;
  }
  roomMembers.value = await store.dispatch(
    'internalRooms/fetchMembers',
    roomId
  );
};

const fetchMessages = async roomId => {
  if (!roomId) return;
  await store.dispatch('internalMessages/fetchForRoom', { roomId });
  await store.dispatch('internalRooms/markRead', roomId);
  scrollToBottom();
};

onMounted(async () => {
  await fetchRooms();
  if (activeRoomId.value) {
    store.dispatch('internalRooms/setActiveRoom', activeRoomId.value);
    await fetchMessages(activeRoomId.value);
    await fetchRoomMembers(activeRoomId.value);
  }
});

onUnmounted(() => {
  store.dispatch('internalRooms/setActiveRoom', null);
  fetchRooms();
});

watch(
  () => route.params.roomId,
  roomId => {
    if (!roomId) {
      selectedRoomId.value = null;
      store.dispatch('internalRooms/setActiveRoom', null);
    }
  }
);

watch(activeRoomId, async roomId => {
  store.dispatch('internalRooms/setActiveRoom', roomId || null);
  if (roomId) {
    await fetchMessages(roomId);
    await fetchRoomMembers(roomId);
  } else {
    roomMembers.value = [];
  }
});

watch(
  () => messages.value.length,
  () => scrollToBottom()
);

const selectRoom = roomId => {
  selectedRoomId.value = roomId;
  router.replace({
    name: 'internal_room_show',
    params: { accountId: route.params.accountId, roomId },
  });
};

const openNewDm = () => {
  newDmModalRef.value?.open();
};

const openEditMembers = () => {
  if (!activeRoom.value) return;
  editGroupMembersModalRef.value?.open({
    id: activeRoom.value.id,
    name: activeRoom.value.name,
    avatarUrl: activeRoom.value.avatar_url,
  });
};

const onMembersSaved = async () => {
  await fetchRooms();
  if (activeRoomId.value) {
    await fetchRoomMembers(activeRoomId.value);
  }
};

const openNewGroup = () => {
  newGroupModalRef.value?.open();
};

const onGroupCreated = async ({ name, userIds }) => {
  if (isCreatingGroup.value) return;
  isCreatingGroup.value = true;
  try {
    const room = await store.dispatch('internalRooms/createGroup', {
      name,
      userIds,
    });
    await fetchRooms();
    if (room?.id) selectRoom(room.id);
  } finally {
    isCreatingGroup.value = false;
  }
};

const onAgentSelected = async agent => {
  if (isCreatingDm.value) return;
  isCreatingDm.value = true;
  try {
    const room = await store.dispatch('internalRooms/createDirect', agent.id);
    await fetchRooms();
    if (room?.id) selectRoom(room.id);
  } finally {
    isCreatingDm.value = false;
  }
};

const sendMessage = async ({ content, files }) => {
  if ((!content && !files?.length) || !activeRoomId.value) return;
  await store.dispatch('internalMessages/send', {
    roomId: activeRoomId.value,
    content,
    files,
  });
  await fetchRooms();
  scrollToBottom();
};
</script>

<template>
  <div class="flex h-full w-full bg-n-background">
    <aside
      v-show="showRoomList"
      class="w-full md:w-72 border-r border-n-weak flex flex-col shrink-0"
    >
      <div
        class="p-4 border-b border-n-weak flex items-center justify-between gap-2"
      >
        <span class="font-medium">{{ t('INTERNAL_CHAT.TITLE') }}</span>
        <div class="flex gap-1 shrink-0">
          <button
            v-if="isAdmin"
            type="button"
            class="px-2 py-1 text-xs rounded-lg border border-n-brand text-n-brand hover:bg-n-alpha-1 disabled:opacity-50"
            :disabled="isCreatingGroup"
            @click="openNewGroup"
          >
            {{ t('INTERNAL_CHAT.NEW_GROUP') }}
          </button>
          <button
            type="button"
            class="px-2 py-1 text-xs rounded-lg bg-n-brand text-white hover:opacity-90 disabled:opacity-50"
            :disabled="isCreatingDm"
            @click="openNewDm"
          >
            {{ t('INTERNAL_CHAT.NEW_DM') }}
          </button>
        </div>
      </div>
      <div class="flex-1 overflow-y-auto">
        <div
          v-if="!rooms.length"
          class="px-4 py-6 text-sm text-n-slate-11 text-center"
        >
          {{ t('INTERNAL_CHAT.NO_ROOMS') }}
        </div>
        <button
          v-for="room in rooms"
          :key="room.id"
          type="button"
          class="w-full text-left px-4 py-3 hover:bg-n-alpha-1 border-b border-n-weak flex items-center gap-3"
          :class="{ 'bg-n-alpha-2': room.id === activeRoomId }"
          @click="selectRoom(room.id)"
        >
          <Avatar
            :name="room.name"
            :src="room.avatar_url"
            :size="36"
            :rounded-full
          />
          <div class="min-w-0 flex-1">
            <div class="font-medium truncate">{{ room.name }}</div>
            <div v-if="room.unread_count > 0" class="text-xs text-n-brand">
              {{ room.unread_count }} {{ t('INTERNAL_CHAT.UNREAD') }}
            </div>
          </div>
        </button>
      </div>
    </aside>
    <section
      v-show="showChatPanel"
      class="flex-1 flex flex-col min-h-0 min-w-0"
    >
      <div
        v-if="!activeRoomId"
        class="flex-1 flex flex-col items-center justify-center gap-4 text-n-slate-11"
      >
        <p>{{ t('INTERNAL_CHAT.SELECT_ROOM') }}</p>
        <div class="flex gap-2">
          <button
            v-if="isAdmin"
            type="button"
            class="px-4 py-2 rounded-lg border border-n-brand text-n-brand hover:bg-n-alpha-1"
            @click="openNewGroup"
          >
            {{ t('INTERNAL_CHAT.NEW_GROUP') }}
          </button>
          <button
            type="button"
            class="px-4 py-2 rounded-lg bg-n-brand text-white hover:opacity-90"
            @click="openNewDm"
          >
            {{ t('INTERNAL_CHAT.NEW_DM') }}
          </button>
        </div>
      </div>
      <template v-else>
        <div
          v-if="isGroupRoom"
          class="px-3 md:px-4 py-3 border-b border-n-weak flex items-center justify-between gap-3"
        >
          <div class="flex items-center gap-2 min-w-0 flex-1">
            <BackButton
              v-if="isMobile"
              :back-url="internalListRoute"
              compact
              class="shrink-0 ltr:-ml-1 rtl:-mr-1"
            />
            <Avatar
              :name="activeRoom?.name"
              :src="activeRoom?.avatar_url"
              :size="32"
              :rounded-full
            />
            <span class="font-medium truncate">{{ activeRoom?.name }}</span>
          </div>
          <button
            v-if="canManageGroup"
            type="button"
            class="px-2 py-1 text-xs rounded-lg border border-n-weak hover:bg-n-alpha-1 shrink-0"
            @click="openEditMembers"
          >
            {{ t('INTERNAL_CHAT.GROUP_SETTINGS') }}
          </button>
        </div>
        <div
          v-if="!isGroupRoom && isMobile"
          class="px-3 py-2 border-b border-n-weak flex items-center gap-2 min-w-0"
        >
          <BackButton
            :back-url="internalListRoute"
            compact
            class="shrink-0 ltr:-ml-1 rtl:-mr-1"
          />
          <Avatar
            :name="activeRoom?.name"
            :src="activeRoom?.avatar_url"
            :size="32"
            :rounded-full
          />
          <span class="font-medium truncate">{{ activeRoom?.name }}</span>
        </div>
        <div
          ref="messagesContainerRef"
          class="flex-1 overflow-y-auto p-4 flex flex-col"
        >
          <div
            v-for="msg in messages"
            :key="msg.id"
            class="flex w-full mb-2 message-bubble-container"
            :class="isSentByMe(msg) ? 'justify-end' : 'justify-start'"
          >
            <div
              class="flex gap-x-2 items-end max-w-lg"
              :class="isSentByMe(msg) ? 'flex-row-reverse' : 'flex-row'"
            >
              <Avatar
                :name="msg.sender?.name"
                :src="msg.sender?.thumbnail"
                :size="24"
                :rounded-full
              />
              <div
                class="text-sm px-4 py-3 max-w-lg"
                :class="
                  isSentByMe(msg)
                    ? 'bg-n-solid-blue text-n-slate-12 rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm'
                    : 'bg-n-slate-4 text-n-slate-12 rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm'
                "
              >
                <div v-if="msg.content" class="whitespace-pre-wrap break-words">
                  {{ msg.content }}
                </div>
                <div
                  v-if="msg.attachments?.length"
                  class="flex flex-col gap-2"
                  :class="{ 'mt-2': msg.content }"
                >
                  <template
                    v-for="attachment in msg.attachments"
                    :key="attachment.id"
                  >
                    <a
                      v-if="isImageAttachment(attachment)"
                      :href="attachment.data_url"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        :src="attachment.thumb_url || attachment.data_url"
                        class="max-w-full rounded-lg max-h-48 object-cover"
                        :alt="t('INTERNAL_CHAT.ATTACHMENT')"
                      />
                    </a>
                    <a
                      v-else
                      :href="attachment.data_url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-xs underline break-all"
                    >
                      {{ t('INTERNAL_CHAT.DOWNLOAD_ATTACHMENT') }}
                    </a>
                  </template>
                </div>
                <div
                  class="text-xs mt-2 text-n-slate-11"
                  :class="isSentByMe(msg) ? 'text-right' : ''"
                >
                  {{ formatMessageTime(msg.created_at) }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <InternalReplyBox :room-id="activeRoomId" @send="sendMessage" />
      </template>
    </section>
    <NewDirectMessageModal ref="newDmModalRef" @select="onAgentSelected" />
    <NewGroupModal ref="newGroupModalRef" @create="onGroupCreated" />
    <EditGroupMembersModal
      ref="editGroupMembersModalRef"
      @saved="onMembersSaved"
    />
  </div>
</template>
